import {
  Flag,
  PATH_SEPARATOR,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
} from "../domain/studioTypes";
import { rowOpeningOf } from "@CommonUtils/cdtStudio/domain/wallRows";
import { compactRowsOf } from "../domain/shelfRankSpace";
import { planBayWidthOf } from "@CommonUtils/cdtStudio/domain/aestheticsScore";
import type {
  ResolverCanvas,
  ResolverCanvasItem,
  ResolverItemsDoc,
  ResolverPin,
} from "./cdtResolve";

export const SIZE_ATTR = "size";
const RESOLVER_SIZE_ATTR = "__sizeLabel";

const PLACEMENT_ID_SEPARATOR = "::";

export const placementId = (skuId: string, index: number): string =>
  index === 0 ? skuId : `${skuId}${PLACEMENT_ID_SEPARATOR}${index}`;

export const baseSkuId = (id: string): string => id.split(PLACEMENT_ID_SEPARATOR)[0];

/** Which of the SKU's placements a plan id stands for: `placementId` read back. */
export const placementIndexOf = (id: string): number =>
  Number(id.split(PLACEMENT_ID_SEPARATOR)[1] ?? 0) || 0;

const resolverAttr = (attr: string, quantityAttribute: string): string =>
  attr === quantityAttribute ? RESOLVER_SIZE_ATTR : attr;

export const RESOLVER_FLAG_KINDS: ReadonlySet<Flag["kind"]> = new Set([
  "orphan",
  "split",
  "contiguity",
  "inversion",
  "size-order",
  "he-order",
  "flavour-order",
]);

const ORDER_FLAG_KINDS: ReadonlySet<Flag["kind"]> = new Set([
  "inversion",
  "size-order",
  "he-order",
  "flavour-order",
]);

export interface ResolverInputs {
  canvas: ResolverCanvas;
  itemsDoc: ResolverItemsDoc;
  pins: readonly ResolverPin[];
  pinByFlagId: Readonly<Record<string, ResolverPin>>;
  runW: number;
  /** Band labels parse to no magnitude and would all tie under the default rule. */
  sizeRule?: "listed";
  quantityAttribute: string;
  verticalBlockShare: number;
  blockIdentityDepths: readonly number[];
  terminalVariantDepth: number | null;
  /** `run|rank` of every pegboard hang line; resolver patches must not land on one. */
  hangLines?: ReadonlySet<string>;
  /** The canvas shelf -> the Studio row rank, when the canvas skips empty hang lines. */
  rankOfShelf?: (shelf: number) => number;
}

export interface ResolverInputOptions {
  quantityAttribute?: string;
  blockIdentityAttributes?: readonly string[];
  verticalBlockShare?: number;
  terminalVariantAttribute?: string;
}

export const flagToPin = (flag: Flag, items: readonly StudioItem[]): ResolverPin => {
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const ids = flag.skuIds.flatMap((skuId) => {
    const item = bySku.get(skuId);
    if (!item) return [skuId];
    return item.placements.map((_, index) => placementId(skuId, index));
  });
  const scope = flag.scopePath.join(PATH_SEPARATOR);
  return {
    kind: flag.kind,
    scope,
    node: scope,
    ids,
    ...(ORDER_FLAG_KINDS.has(flag.kind) && ids.length > 1 ? { seqOrder: ids } : {}),
  };
};

export const toResolverInputs = (
  allItems: readonly StudioItem[],
  allRuns: readonly StudioRunGeometry[],
  draft: StudioDraft,
  flags: readonly Flag[],
  options: ResolverInputOptions = {},
): ResolverInputs => {
  // The resolver's contiguity reads adjacent ranks, so it works on the compact rows.
  const rows = compactRowsOf(allRuns, allItems);
  const { items, runs } = rows;
  const quantityAttribute = options.quantityAttribute ?? SIZE_ATTR;
  const remapAttr = (attr: string) => resolverAttr(attr, quantityAttribute);
  const spine = draft.spine.map((level) => remapAttr(level.attr));
  // Empty identity list is a broken mapping — treat as unconfigured, not "no audits".
  const filteredIdentity = options.blockIdentityAttributes?.filter((attr) =>
    draft.spine.some((level) => level.attr === attr),
  );
  const blockIdentityAttributes = filteredIdentity?.length
    ? filteredIdentity
    : draft.spine.slice(0, 2).map((level) => level.attr);
  const blockIdentityDepths = blockIdentityAttributes
    .map((attr) => draft.spine.findIndex((level) => level.attr === attr) + 1)
    .filter((depth) => depth > 0)
    .sort((a, b) => a - b);
  const terminalVariantIndex = options.terminalVariantAttribute
    ? draft.spine.findIndex((level) => level.attr === options.terminalVariantAttribute)
    : -1;

  const orient: Record<string, "horizontal" | "vertical"> = {};
  for (const level of draft.spine) orient[remapAttr(level.attr)] = level.orient;

  const order: Record<string, readonly string[]> = {};
  for (const [attr, values] of Object.entries(draft.valueOrder)) {
    order[remapAttr(attr)] = [...values];
  }

  const itemsDocEntries: { id: string; a: Record<string, string | null> }[] = [];
  const runItems = new Map<number, ResolverCanvasItem[]>();
  for (const item of items) {
    const attrs = effectiveAttrs(item, draft);
    const remapped: Record<string, string | null> = {};
    for (const [attr, value] of Object.entries(attrs)) remapped[remapAttr(attr)] = value;
    const node = blockIdentityAttributes.map((attr) => attrs[attr] ?? "").join(PATH_SEPARATOR);
    item.placements.forEach((placement, index) => {
      const id = placementId(item.skuId, index);
      itemsDocEntries.push({ id, a: remapped });
      const list = runItems.get(placement.run) ?? [];
      list.push({
        id,
        shelf: placement.shelf,
        x: placement.xCm,
        w: item.facingWidthCm,
        hf: placement.hFacings,
        h: item.heightCm,
        node,
        ix: list.length,
        desc: item.name,
      });
      runItems.set(placement.run, list);
    });
  }

  const geo = runs.map((run) => {
    const byRank = [...run.shelves].sort((a, b) => a.rank - b.rank);
    return {
      shelves: byRank.map((shelf) => ({
        rank: shelf.rank,
        pitch: rowOpeningOf(run, shelf.rank) ?? Infinity,
      })),
    };
  });

  const pins = flags.map((flag) => flagToPin(flag, items));
  const pinByFlagId: Record<string, ResolverPin> = {};
  flags.forEach((flag, index) => {
    pinByFlagId[flag.id] = pins[index];
  });

  return {
    canvas: {
      runs: [...runItems.entries()]
        .sort(([a], [b]) => a - b)
        .map(([run, list]) => ({ run, items: list })),
      geo,
      bayW: planBayWidthOf(runs),
      pins,
    },
    itemsDoc: {
      ITEMS: itemsDocEntries,
      SPINE: spine,
      ORIENT: orient,
      ORDER: order,
      LABELS: Object.fromEntries(draft.spine.map((level) => [remapAttr(level.attr), level.label])),
    },
    pins,
    pinByFlagId,
    runW: Math.max(...runs.map((run) => run.widthCm), 0) || planBayWidthOf(runs) * 11,
    sizeRule: draft.levelBands?.[quantityAttribute] ? "listed" : undefined,
    quantityAttribute,
    verticalBlockShare: options.verticalBlockShare ?? 0.55,
    blockIdentityDepths,
    terminalVariantDepth: terminalVariantIndex >= 0 ? terminalVariantIndex : null,
    rankOfShelf: rows.active ? rows.rankOf : undefined,
    hangLines: new Set(
      allRuns.flatMap((run) =>
        run.shelves.filter((row) => row.pegboard).map((row) => `${run.run}|${row.rank}`),
      ),
    ),
  };
};
